import React, { useState, useEffect } from 'react';
import liveUpdates from '../liveUpdates';
import { useAdmin, adminFetch } from '../admin';

const AdminSignIn = () => {
    const { login } = useAdmin();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setBusy(true); setError('');
        const result = await login(password);
        setBusy(false);
        if (!result.ok) setError(result.error);
        setPassword('');
    };

    return (
        <div>
            <header className="page-header">
                <div className="container"><h1>Admin sign in</h1><p>This page is only for the league admin.</p></div>
            </header>
            <main className="container mt-5" style={{ maxWidth: '420px' }}>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="admin-password">Admin password</label>
                        <input id="admin-password" type="password" className="form-control" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Signing in...' : 'Sign in'}</button>
                </form>
            </main>
        </div>
    );
};

const GameApprovalPage = () => {
    const { isAdmin, checked } = useAdmin();
    if (!checked) return <div className="container mt-5"><div className="alert alert-info">Loading...</div></div>;
    return isAdmin ? <ApprovalDashboard /> : <AdminSignIn />;
};

const ApprovalDashboard = () => {
    const { expire, logout } = useAdmin();
    const [games, setGames] = useState([]);
    const [filter, setFilter] = useState('all');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const [loading, setLoading] = useState(true);

    const fetchGames = () => {
        setLoading(true);
        fetch('/api/orders')
            .then(res => res.json())
            .then(data => { setGames(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(err => { console.error('Error fetching games:', err); setLoading(false); });
    };

    useEffect(() => {
        fetchGames();

        // Refresh when games change (checked every 20 seconds)
        liveUpdates.on('orders_updated', fetchGames);

        return () => liveUpdates.off('orders_updated', fetchGames);
    }, []);

    const approveGame = (gameId) => {
        adminFetch(`/api/orders/${encodeURIComponent(gameId)}/approve`, { method: 'PUT' }, expire)
            .then(data => { if (data.success) { showMessage('Game approved successfully!', 'success'); fetchGames(); } })
            .catch(err => console.error('Error approving game:', err));
    };

    const declineGame = (gameId) => {
        adminFetch(`/api/orders/${encodeURIComponent(gameId)}/decline`, { method: 'PUT' }, expire)
            .then(data => { if (data.success) { showMessage('Game declined.', 'warning'); fetchGames(); } })
            .catch(err => console.error('Error declining game:', err));
    };

    const deleteGame = (gameId) => {
        if (window.confirm('Are you sure you want to delete this game? This action cannot be undone.')) {
            adminFetch(`/api/orders/${encodeURIComponent(gameId)}`, { method: 'DELETE' }, expire)
                .then(data => { if (data.success) { showMessage('Game deleted successfully!', 'danger'); fetchGames(); } })
                .catch(err => console.error('Error deleting game:', err));
        }
    };

    const showMessage = (msg, type = 'success') => {
        setMessage(msg); setMessageType(type);
        setTimeout(() => setMessage(''), 5000);
    };

    const filteredGames = filter === 'all' ? games : games.filter(game => game.status === filter);

    return (
        <div>
            <header className="page-header">
                <div className="container d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div><h1>Game Approval Management</h1><p>Review and manage all game requests.</p></div>
                    <button type="button" className="btn btn-outline-light btn-sm" onClick={logout}>Sign out</button>
                </div>
            </header>
            <main className="container mt-5">
                {message && (
                    <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
                        {message}<button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                    </div>
                )}
                <div className="mb-4">
                    <h3>Filter by Status</h3>
                    <div className="btn-group" role="group">
                        {['all', 'pending', 'approved', 'declined'].map(status => (
                            <button key={status} type="button" className={`btn ${filter === status ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter(status)}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                {loading ? <div className="alert alert-info">Loading games...</div> : filteredGames.length === 0 ? <div className="alert alert-info">No games found.</div> : (
                    <div className="row">
                        {filteredGames.map(game => (
                            <div className="col-md-6 col-lg-4 mb-4" key={game.id}>
                                <div className="card shadow-sm">
                                    <div className="card-header bg-light">
                                        <h5 className="mb-0">{game.gameName || 'Unnamed Game'}</h5>
                                        <small className="text-muted">Sport: {game.sport}</small>
                                    </div>
                                    <div className="card-body">
                                        <p><strong>Location:</strong> {game.location || 'N/A'}</p>
                                        <p><strong>Date & Time:</strong> {game.dateTime ? new Date(game.dateTime).toLocaleString() : 'N/A'}</p>
                                        <p><strong>Order ID:</strong> {game.id}</p>
                                        <p><strong>Status:</strong>{' '}
                                            <span className={`badge bg-${game.status === 'approved' ? 'success' : game.status === 'declined' ? 'danger' : 'warning'}`}>
                                                {game.status || 'pending'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="card-footer">
                                        {(game.status === 'pending' || !game.status) && (
                                            <>
                                                <button className="btn btn-sm btn-success me-2" onClick={() => approveGame(game.id)}>Approve</button>
                                                <button className="btn btn-sm btn-danger me-2" onClick={() => declineGame(game.id)}>Decline</button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteGame(game.id)}>Delete</button>
                                            </>
                                        )}
                                        {game.status === 'approved' && (
                                            <>
                                                <span className="text-success fw-bold me-2">✓ Approved</span>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteGame(game.id)}>Delete</button>
                                            </>
                                        )}
                                        {game.status === 'declined' && (
                                            <>
                                                <span className="text-danger fw-bold me-2">✗ Declined</span>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteGame(game.id)}>Delete</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default GameApprovalPage;
